import { useEffect, useState, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import { api, fileUrl, FUEL_TYPES, VEHICLE_STATUSES, SERVICE_TYPES } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useI18n } from "../../i18n/I18nProvider";
import { toast } from "sonner";
import {
  Truck, ArrowLeft, Gauge, Leaf, CalendarClock, Wrench, FileText,
  Upload, Trash2, Plus, Pencil, Save, X, CheckCircle, AlertTriangle,
} from "lucide-react";

function Stat({ label, value, testid }) {
  return (
    <div className="border border-[var(--ink-200)] bg-[var(--bone)] p-4" data-testid={testid}>
      <div className="label-tech mb-1">{label}</div>
      <div className="font-display text-lg">{value}</div>
    </div>
  );
}

export default function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  const [vehicle, setVehicle] = useState(null);
  const [inspections, setInspections] = useState([]);
  const [services, setServices] = useState([]);
  const [co2Records, setCo2Records] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [orgUsers, setOrgUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({});
  const fileInputRef = useRef(null);

  const canEdit = user && vehicle && (user.role === "admin" || user.user_id === vehicle.responsible_user_id || user.user_id === vehicle.owner_id);

  const load = async () => {
    const { data } = await api.get(`/vehicles/${id}`);
    setVehicle(data);
    setForm({
      regnr: data.regnr || "",
      brand: data.brand || "",
      model: data.model || "",
      year: data.year || "",
      fueltype: data.fueltype || "diesel",
      consumption: data.consumption ?? "",
      odometer: data.odometer ?? "",
      responsible_user_id: data.responsible_user_id || "",
      inspection_org: data.inspection_org || "",
      workshop: data.workshop || "",
      next_inspection: data.next_inspection ? data.next_inspection.slice(0, 10) : "",
      next_service: data.next_service ? data.next_service.slice(0, 10) : "",
      status: data.status || "aktiv",
      notes: data.notes || "",
    });
  };

  useEffect(() => {
    if (!user || !id) return;
    load().catch(() => toast.error(t("detail.notFound")));
    api.get(`/vehicles/${id}/inspections`).then((r) => setInspections(r.data || [])).catch(() => {});
    api.get(`/vehicles/${id}/services`).then((r) => setServices(r.data || [])).catch(() => {});
    api.get(`/vehicles/${id}/co2-records`).then((r) => setCo2Records(r.data || [])).catch(() => {});
    api.get(`/vehicle-reminders?vehicle_id=${id}&days=365`).then((r) => setReminders(r.data || [])).catch(() => {});
    api.get("/org/members").then((r) => setOrgUsers(r.data || [])).catch(() => {});
    api.get("/projects").then((r) => setProjects(r.data || [])).catch(() => {});
  }, [user, id]);

  if (!user) return <Layout><div className="max-w-[1440px] mx-auto p-10 label-tech">{t("add.loginRequired")}</div></Layout>;
  if (!vehicle) return <Layout><div className="max-w-[1440px] mx-auto p-10 label-tech">{t("detail.loading")}</div></Layout>;

  const save = async () => {
    const payload = {
      ...form,
      year: form.year ? Number(form.year) : null,
      consumption: form.consumption !== "" ? Number(form.consumption) : null,
      odometer: form.odometer !== "" ? Number(form.odometer) : 0,
      responsible_user_id: form.responsible_user_id || null,
      next_inspection: form.next_inspection ? new Date(form.next_inspection).toISOString() : null,
      next_service: form.next_service ? new Date(form.next_service).toISOString() : null,
    };
    try {
      await api.patch(`/vehicles/${id}`, payload);
      toast.success(t("veh.updated"));
      setEdit(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("dash.failed"));
    }
  };

  const remove = async () => {
    if (!window.confirm(t("veh.confirmDelete"))) return;
    try {
      await api.delete(`/vehicles/${id}`);
      toast.success(t("veh.deleted"));
      navigate("/vehicles/list");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("dash.failed"));
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/vehicles/${id}/documents`, { path: data.path, label: file.name });
      toast.success(t("veh.uploaded"));
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("add.uploadFail"));
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const detachDoc = async (path) => {
    try {
      await api.delete(`/vehicles/${id}/documents`, { params: { path } });
      toast.success(t("admin.actionOk"));
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("dash.failed"));
    }
  };

  const responsibleName = orgUsers.find((u) => u.user_id === vehicle.responsible_user_id)?.name
    || orgUsers.find((u) => u.user_id === vehicle.responsible_user_id)?.email
    || "—";
  const overdue = vehicle.next_inspection && vehicle.next_inspection < new Date().toISOString();

  return (
    <Layout>
      <div className="max-w-[1440px] mx-auto px-6 pt-10 pb-16">
        <Link to="/vehicles/list" className="label-tech inline-flex items-center gap-1 mb-4">
          <ArrowLeft size={12} /> {t("nav.backToList")}
        </Link>

        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div className="label-tech inline-flex items-center gap-2">
              <Truck size={12} /> {t(`veh.fuel.${vehicle.fueltype}`)} · {vehicle.year || "—"}
              {overdue && <span className="inline-flex items-center gap-1 text-[var(--brick)] ml-2"><AlertTriangle size={12} /> {t("veh.outOfCompliance")}</span>}
            </div>
            <h1 className="font-display font-mono-tech text-4xl md:text-5xl tracking-tighter mt-2" data-testid="vehicle-regnr">{vehicle.regnr}</h1>
            <div className="text-[var(--ink-700)] mt-1">{vehicle.brand} {vehicle.model}</div>
          </div>
          {canEdit && !edit && (
            <div className="flex items-center gap-2">
              <button onClick={() => setEdit(true)} className="btn-ghost" data-testid="vehicle-edit-btn">
                <Pencil size={14} /> {t("admin.orgs.edit")}
              </button>
              <button onClick={remove} className="btn-ghost text-[var(--brick)]" data-testid="vehicle-delete-btn">
                <Trash2 size={14} /> {t("admin.orgs.delete")}
              </button>
            </div>
          )}
        </div>

        {/* Info / edit form */}
        {edit ? (
          <div className="border border-[var(--ink-200)] bg-[var(--bone)] p-5 grid grid-cols-1 md:grid-cols-3 gap-4 mb-8" data-testid="vehicle-edit-form">
            <div><div className="label-tech mb-2">{t("veh.regnr")}</div>
              <input className="input-tech" value={form.regnr} onChange={(e) => setForm({ ...form, regnr: e.target.value.toUpperCase() })} /></div>
            <div><div className="label-tech mb-2">{t("veh.brand")}</div>
              <input className="input-tech" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} /></div>
            <div><div className="label-tech mb-2">{t("veh.model")}</div>
              <input className="input-tech" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} /></div>
            <div><div className="label-tech mb-2">{t("veh.year")}</div>
              <input type="number" className="input-tech" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} /></div>
            <div><div className="label-tech mb-2">{t("veh.fueltype")}</div>
              <select className="input-tech" value={form.fueltype} onChange={(e) => setForm({ ...form, fueltype: e.target.value })}>
                {FUEL_TYPES.map((f) => <option key={f} value={f}>{t(`veh.fuel.${f}`)}</option>)}
              </select></div>
            <div><div className="label-tech mb-2">{t("veh.consumption")}</div>
              <input type="number" step="0.1" className="input-tech" value={form.consumption} onChange={(e) => setForm({ ...form, consumption: e.target.value })} /></div>
            <div><div className="label-tech mb-2">{t("veh.odometer")}</div>
              <input type="number" className="input-tech" value={form.odometer} onChange={(e) => setForm({ ...form, odometer: e.target.value })} /></div>
            <div><div className="label-tech mb-2">{t("veh.responsible")}</div>
              <select className="input-tech" value={form.responsible_user_id} onChange={(e) => setForm({ ...form, responsible_user_id: e.target.value })}>
                <option value="">{t("veh.responsiblePh")}</option>
                {orgUsers.map((u) => <option key={u.user_id} value={u.user_id}>{u.name || u.email}</option>)}
              </select></div>
            <div><div className="label-tech mb-2">{t("veh.status")}</div>
              <select className="input-tech" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                {VEHICLE_STATUSES.map((s) => <option key={s} value={s}>{t(`veh.status.${s}`)}</option>)}
              </select></div>
            <div><div className="label-tech mb-2">{t("veh.inspectionOrg")}</div>
              <input className="input-tech" value={form.inspection_org} onChange={(e) => setForm({ ...form, inspection_org: e.target.value })} /></div>
            <div><div className="label-tech mb-2">{t("veh.workshop")}</div>
              <input className="input-tech" value={form.workshop} onChange={(e) => setForm({ ...form, workshop: e.target.value })} /></div>
            <div><div className="label-tech mb-2">{t("veh.nextInspection")}</div>
              <input type="date" className="input-tech" value={form.next_inspection} onChange={(e) => setForm({ ...form, next_inspection: e.target.value })} /></div>
            <div><div className="label-tech mb-2">{t("veh.nextService")}</div>
              <input type="date" className="input-tech" value={form.next_service} onChange={(e) => setForm({ ...form, next_service: e.target.value })} /></div>
            <div className="md:col-span-3">
              <div className="label-tech mb-2">{t("veh.notes")}</div>
              <textarea className="input-tech" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <div className="md:col-span-3 flex justify-end gap-2">
              <button onClick={() => setEdit(false)} className="btn-ghost"><X size={14} /> {t("admin.orgs.cancel")}</button>
              <button onClick={save} className="btn-primary" data-testid="vehicle-save-btn"><Save size={14} /> {t("admin.orgs.save")}</button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Stat label={t("veh.responsible")} value={responsibleName} testid="vehicle-stat-responsible" />
            <Stat label={t("veh.odometer")} value={`${Math.round(vehicle.odometer || 0).toLocaleString()} km`} testid="vehicle-stat-odometer" />
            <Stat label={t("veh.nextInspection")} value={vehicle.next_inspection ? vehicle.next_inspection.slice(0, 10) : "—"} testid="vehicle-stat-nextins" />
            <Stat label={t("veh.nextService")} value={vehicle.next_service ? vehicle.next_service.slice(0, 10) : "—"} testid="vehicle-stat-nextsvc" />
          </div>
        )}

        {/* Sub-sections: Inspections / Services / CO2 / Documents */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <InspectionSection vehicle={vehicle} inspections={inspections} canEdit={canEdit} onChange={async () => { await load(); api.get(`/vehicles/${id}/inspections`).then((r) => setInspections(r.data || [])); }} />
          <ServiceSection vehicle={vehicle} services={services} canEdit={canEdit} onChange={async () => { await load(); api.get(`/vehicles/${id}/services`).then((r) => setServices(r.data || [])); }} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <Co2Section vehicle={vehicle} co2Records={co2Records} projects={projects} canEdit={canEdit} onChange={() => api.get(`/vehicles/${id}/co2-records`).then((r) => setCo2Records(r.data || []))} />
          <DocsSection
            vehicle={vehicle}
            canEdit={canEdit}
            onUpload={() => fileInputRef.current?.click()}
            onDetach={detachDoc}
          />
        </div>

        {/* Reminders */}
        <div className="border border-[var(--ink-200)] bg-[var(--bone)] p-5" data-testid="vehicle-reminders">
          <div className="label-tech mb-3">{t("vrem.title")}</div>
          {reminders.length === 0 ? (
            <div className="text-sm text-[var(--ink-500)]">{t("vrem.empty")}</div>
          ) : (
            <div className="divide-y divide-[var(--ink-200)]">
              {reminders.map((r) => (
                <div key={r.id} className="py-2 flex items-center justify-between text-sm">
                  <span className="inline-flex items-center gap-2">
                    <CalendarClock size={12} /> {t(`vrem.type.${r.reminder_type}`)}
                  </span>
                  <span className="font-mono-tech text-[var(--ink-700)]">{r.due_date.slice(0, 10)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <input ref={fileInputRef} type="file" className="hidden" onChange={handleUpload} data-testid="vehicle-upload-input" />
      </div>
    </Layout>
  );
}

function InspectionSection({ vehicle, inspections, canEdit, onChange }) {
  const { t } = useI18n();
  const [showForm, setShowForm] = useState(false);
  const [date, setDate] = useState("");

  const book = async () => {
    if (!date) return;
    try {
      await api.post("/vehicle-inspections", {
        vehicle_id: vehicle.id,
        date: new Date(date).toISOString(),
      });
      toast.success(t("vins.bookNew"));
      setShowForm(false);
      setDate("");
      await onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("dash.failed"));
    }
  };

  const setResult = async (insp, result) => {
    let next_inspection = null;
    if (result === "pass") {
      const next = new Date();
      next.setFullYear(next.getFullYear() + 1);
      next_inspection = next.toISOString();
    }
    try {
      await api.patch(`/vehicle-inspections/${insp.id}`, { result, next_inspection });
      toast.success(t("veh.updated"));
      if (result === "fail") toast.message(t("vins.createdReinspection"));
      await onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("dash.failed"));
    }
  };

  return (
    <div className="border border-[var(--ink-200)] bg-[var(--bone)] p-5" data-testid="vehicle-inspections">
      <div className="flex items-center justify-between mb-3">
        <div className="label-tech inline-flex items-center gap-2"><CalendarClock size={12} /> {t("vins.title")}</div>
        {canEdit && (
          <button onClick={() => setShowForm((s) => !s)} className="btn-ghost text-xs" data-testid="vehicle-book-inspection-btn">
            <Plus size={12} /> {t("vins.bookNew")}
          </button>
        )}
      </div>
      {showForm && (
        <div className="mb-3 flex flex-wrap gap-2 items-end">
          <input type="date" className="input-tech max-w-[180px]" value={date} onChange={(e) => setDate(e.target.value)} data-testid="vehicle-inspection-date-input" />
          <button onClick={book} className="btn-primary" data-testid="vehicle-inspection-submit">{t("admin.orgs.save")}</button>
          <button onClick={() => setShowForm(false)} className="btn-ghost">{t("admin.orgs.cancel")}</button>
        </div>
      )}
      {inspections.length === 0 ? (
        <div className="text-sm text-[var(--ink-500)]">{t("vins.empty")}</div>
      ) : (
        <div className="divide-y divide-[var(--ink-200)]">
          {inspections.slice(0, 8).map((i) => (
            <div key={i.id} className="py-2 flex flex-wrap items-center justify-between gap-2 text-sm" data-testid={`inspection-row-${i.id}`}>
              <div className="flex items-center gap-2">
                <span className="font-mono-tech">{i.date?.slice(0, 10)}</span>
                {i.is_reinspection && <span className="text-[10px] uppercase font-bold tracking-wider text-[var(--clay)]">{t("vins.reinspection")}</span>}
                {i.result === "pass" && <span className="inline-flex items-center gap-1 text-[var(--moss)]"><CheckCircle size={12} /> {t("vins.pass")}</span>}
                {i.result === "fail" && <span className="inline-flex items-center gap-1 text-[var(--brick)]"><AlertTriangle size={12} /> {t("vins.fail")}</span>}
                {!i.result && <span className="text-[var(--ink-500)]">{t("vins.pending")}</span>}
              </div>
              {canEdit && !i.result && (
                <div className="flex gap-2">
                  <button onClick={() => setResult(i, "pass")} className="btn-ghost text-xs text-[var(--moss)]" data-testid={`inspection-pass-${i.id}`}>{t("vins.markPass")}</button>
                  <button onClick={() => setResult(i, "fail")} className="btn-ghost text-xs text-[var(--brick)]" data-testid={`inspection-fail-${i.id}`}>{t("vins.markFail")}</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ServiceSection({ vehicle, services, canEdit, onChange }) {
  const { t } = useI18n();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ date: "", service_type: "regular", workshop: "", cost: "" });

  const book = async () => {
    if (!form.date) return;
    try {
      await api.post("/vehicle-services", {
        vehicle_id: vehicle.id,
        date: new Date(form.date).toISOString(),
        service_type: form.service_type,
        workshop: form.workshop || null,
        cost: form.cost ? Number(form.cost) : null,
      });
      toast.success(t("vsvc.bookNew"));
      setShowForm(false);
      setForm({ date: "", service_type: "regular", workshop: "", cost: "" });
      await onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("dash.failed"));
    }
  };

  const markDone = async (srv) => {
    try {
      await api.patch(`/vehicle-services/${srv.id}`, { completed: true });
      toast.success(t("vsvc.markCompleted"));
      await onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("dash.failed"));
    }
  };

  return (
    <div className="border border-[var(--ink-200)] bg-[var(--bone)] p-5" data-testid="vehicle-services">
      <div className="flex items-center justify-between mb-3">
        <div className="label-tech inline-flex items-center gap-2"><Wrench size={12} /> {t("vsvc.title")}</div>
        {canEdit && (
          <button onClick={() => setShowForm((s) => !s)} className="btn-ghost text-xs" data-testid="vehicle-book-service-btn">
            <Plus size={12} /> {t("vsvc.bookNew")}
          </button>
        )}
      </div>
      {showForm && (
        <div className="mb-3 grid grid-cols-2 gap-2">
          <input type="date" className="input-tech" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="vehicle-service-date-input" />
          <select className="input-tech" value={form.service_type} onChange={(e) => setForm({ ...form, service_type: e.target.value })}>
            {SERVICE_TYPES.map((s) => <option key={s} value={s}>{t(`vsvc.type.${s}`)}</option>)}
          </select>
          <input placeholder={t("veh.workshop")} className="input-tech" value={form.workshop} onChange={(e) => setForm({ ...form, workshop: e.target.value })} />
          <input type="number" placeholder={t("vsvc.cost")} className="input-tech" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} />
          <div className="col-span-2 flex justify-end gap-2">
            <button onClick={() => setShowForm(false)} className="btn-ghost">{t("admin.orgs.cancel")}</button>
            <button onClick={book} className="btn-primary" data-testid="vehicle-service-submit">{t("admin.orgs.save")}</button>
          </div>
        </div>
      )}
      {services.length === 0 ? (
        <div className="text-sm text-[var(--ink-500)]">{t("vsvc.empty")}</div>
      ) : (
        <div className="divide-y divide-[var(--ink-200)]">
          {services.slice(0, 8).map((s) => (
            <div key={s.id} className="py-2 flex flex-wrap items-center justify-between gap-2 text-sm" data-testid={`service-row-${s.id}`}>
              <div className="flex items-center gap-2">
                <span className="font-mono-tech">{s.date?.slice(0, 10)}</span>
                <span className="text-[var(--ink-500)]">{t(`vsvc.type.${s.service_type || "regular"}`)}</span>
                {s.completed ? (
                  <span className="inline-flex items-center gap-1 text-[var(--moss)]"><CheckCircle size={12} /> {t("vsvc.completed")}</span>
                ) : null}
              </div>
              {canEdit && !s.completed && (
                <button onClick={() => markDone(s)} className="btn-ghost text-xs" data-testid={`service-mark-done-${s.id}`}>{t("vsvc.markCompleted")}</button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Co2Section({ vehicle, co2Records, projects, canEdit, onChange }) {
  const { t } = useI18n();
  const [showForm, setShowForm] = useState(false);
  const now = new Date();
  const [form, setForm] = useState({
    period_type: "month",
    period: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`,
    distance_km: "",
    project_id: "",
  });

  const submit = async () => {
    if (!form.distance_km) return;
    try {
      await api.post("/vehicle-co2/aggregate", {
        vehicle_id: vehicle.id,
        period_type: form.period_type,
        period: form.period,
        distance_km: Number(form.distance_km),
        project_id: form.project_id || null,
      });
      toast.success(t("vco2.aggregated"));
      setShowForm(false);
      setForm({ ...form, distance_km: "" });
      await onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("dash.failed"));
    }
  };

  const total = co2Records.reduce((acc, r) => acc + (r.co2_kg || 0), 0);

  return (
    <div className="border border-[var(--ink-200)] bg-[var(--bone)] p-5" data-testid="vehicle-co2">
      <div className="flex items-center justify-between mb-3">
        <div className="label-tech inline-flex items-center gap-2"><Leaf size={12} /> {t("vco2.title")}</div>
        {canEdit && (
          <button onClick={() => setShowForm((s) => !s)} className="btn-ghost text-xs" data-testid="vehicle-add-co2-btn">
            <Plus size={12} /> {t("vco2.addRecord")}
          </button>
        )}
      </div>
      {showForm && (
        <div className="mb-3 grid grid-cols-2 gap-2">
          <select className="input-tech" value={form.period_type} onChange={(e) => setForm({ ...form, period_type: e.target.value })}>
            <option value="month">{t("vco2.period.month")}</option>
            <option value="quarter">{t("vco2.period.quarter")}</option>
            <option value="year">{t("vco2.period.year")}</option>
          </select>
          <input placeholder="2026-05" className="input-tech" value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} />
          <input type="number" placeholder={t("vco2.distance")} className="input-tech" value={form.distance_km} onChange={(e) => setForm({ ...form, distance_km: e.target.value })} data-testid="vehicle-co2-distance-input" />
          <select className="input-tech" value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })}>
            <option value="">{t("vco2.allProjects")}</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.namn}</option>)}
          </select>
          <div className="col-span-2 flex justify-end gap-2">
            <button onClick={() => setShowForm(false)} className="btn-ghost">{t("admin.orgs.cancel")}</button>
            <button onClick={submit} className="btn-primary" data-testid="vehicle-co2-submit">{t("admin.orgs.save")}</button>
          </div>
        </div>
      )}
      <div className="text-2xl font-display tracking-tighter mb-2" data-testid="vehicle-co2-total">
        {Math.round(total).toLocaleString()} <span className="text-sm text-[var(--ink-500)]">{t("vco2.kgCO2")}</span>
      </div>
      {co2Records.length === 0 ? (
        <div className="text-sm text-[var(--ink-500)]">{t("vco2.empty")}</div>
      ) : (
        <div className="divide-y divide-[var(--ink-200)] max-h-48 overflow-auto">
          {co2Records.slice(0, 10).map((r) => (
            <div key={r.id} className="py-2 flex items-center justify-between text-sm">
              <span className="font-mono-tech">{r.period}</span>
              <span className="text-[var(--ink-700)]">{Math.round(r.distance_km).toLocaleString()} km</span>
              <span className="font-mono-tech text-[var(--moss)]">{Math.round(r.co2_kg).toLocaleString()} kg</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DocsSection({ vehicle, canEdit, onUpload, onDetach }) {
  const { t } = useI18n();
  const docs = vehicle.documents || [];
  return (
    <div className="border border-[var(--ink-200)] bg-[var(--bone)] p-5" data-testid="vehicle-documents">
      <div className="flex items-center justify-between mb-3">
        <div className="label-tech inline-flex items-center gap-2"><FileText size={12} /> {t("veh.documents")}</div>
        {canEdit && (
          <button onClick={onUpload} className="btn-ghost text-xs" data-testid="vehicle-upload-btn">
            <Upload size={12} /> {t("veh.uploadProtocol")}
          </button>
        )}
      </div>
      {docs.length === 0 ? (
        <div className="text-sm text-[var(--ink-500)]">—</div>
      ) : (
        <div className="space-y-2">
          {docs.map((d, idx) => {
            const path = typeof d === "string" ? d : d.path;
            const label = typeof d === "string" ? d : (d.label || path);
            return (
              <div key={`${path}-${idx}`} className="flex items-center justify-between text-sm">
                <a href={fileUrl(path)} target="_blank" rel="noreferrer" className="underline decoration-dotted truncate max-w-[80%]">{label}</a>
                {canEdit && (
                  <button onClick={() => onDetach(path)} className="btn-ghost text-xs text-[var(--brick)]" data-testid="vehicle-doc-detach">
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
